// IMPORTANT: When adding new event types to the backend (src/events/enums/event-type.enum.ts),
// you MUST also add them to this list to ensure they display with proper colors!
// Also update the list in:
// - src/admin/pages/AdminEventsPage.tsx (EVENT_TYPE_OPTIONS)
// - src/components/Profile/Notifications.tsx (ALL_EVENT_TYPES)
export const EVENT_TYPE_COLORS: Record<string, string> = {
    // User events
    'USER_REGISTERED': 'green',
    'USER_LOGIN': 'blue',
    'USER_LOGOUT': 'default',
    'USER_PROFILE_UPDATED': 'orange',
    'USER_PASSWORD_CHANGED': 'orange',
    'USER_PASSWORD_SET': 'green',
    'USER_IMPERSONATED': 'purple',
    'USER_IMPERSONATION_STARTED': 'purple',
    'USER_IMPERSONATION_ENDED': 'purple',
    'USER_LOGIN_FAILED': 'red',
    'USER_ACCOUNT_LOCKED': 'red',
    'USER_ACCOUNT_UNLOCKED': 'green',

    // Organization events
    'ORGANIZATION_CREATED': 'green',
    'ORGANIZATION_UPDATED': 'blue',
    'ORGANIZATION_STATUS_UPDATED': 'orange',
    'ORGANIZATION_SETTINGS_CHANGED': 'orange',
    'ORGANIZATION_FEATURES_UPDATED': 'blue',
    'ORGANIZATION_DELETED': 'red',

    // Brand events
    'BRAND_CREATED': 'green',
    'BRAND_UPDATED': 'blue',
    'BRAND_NAME_UPDATED': 'blue',
    'BRAND_DELETED': 'red',

    // Cohort events
    'COHORT_CREATED': 'green',
    'COHORT_UPDATED': 'blue',
    'COHORT_DEACTIVATED': 'orange',
    'COHORT_DELETED': 'red',

    // Survey events
    'SURVEY_CREATED': 'green',
    'SURVEY_UPDATED': 'blue',
    'SURVEY_PUBLISHED': 'green',
    'SURVEY_PAUSED': 'orange',
    'SURVEY_DELETED': 'red',
    'SURVEY_RESPONSE_RECEIVED': 'cyan',
    'SURVEY_RESPONSE_DELETED': 'red',
    'SURVEY_LINK_NOT_FOUND': 'red',
    'SURVEY_LINK_SALVAGED': 'orange',

    // Admin events
    'ADMIN_USER_CREATED': 'green',
    'ADMIN_USER_UPDATED': 'blue',
    'ADMIN_USER_DELETED': 'red',
    'ADMIN_USER_PASSWORD_RESET': 'orange',

    // Organization user events
    'ORGANIZATION_USER_CREATED': 'green',

    // System events
    'API_KEY_CREATED': 'green',
    'API_KEY_REVOKED': 'red',
    'PUBLIC_API_PARTICIPANTS_FETCHED': 'cyan',
    'BILLING_EVENT': 'gold',
    'DATA_EXPORT_REQUESTED': 'purple',
    'SYSTEM_MAINTENANCE': 'orange',
    'DATABASE_BACKUP_CREATED': 'red',
    'DATABASE_BACKUP_COMPLETED': 'green',
    'DATABASE_BACKUP_FAILED': 'red',
    'DATABASE_BACKUP_DELETED': 'orange',
    'DATABASE_BACKUP_RETENTION_CLEANUP': 'blue',
    'RATE_LIMIT_SETTINGS_UPDATED': 'orange',
    'RATE_LIMIT_EXCEEDED': 'red',

    // Message events
    'MESSAGE_SENT_BY_ADMIN': 'blue',
    'MESSAGE_RECEIVED': 'cyan',
    'MESSAGE_READ': 'green',
    'MESSAGE_MARKED_UNREAD': 'default',
    'MESSAGE_STARRED': 'gold',
    'MESSAGE_UNSTARRED': 'default',
    'MESSAGE_ARCHIVED': 'orange',
    'MESSAGE_UNARCHIVED': 'blue',
    'MESSAGE_DELETED': 'red',
    'MESSAGE_PERMANENTLY_DELETED': 'red',
    'MESSAGES_BULK_UPDATED': 'blue',
    'MESSAGES_ALL_MARKED_READ': 'blue',
    'MESSAGES_TRASH_EMPTIED': 'orange',
    'MESSAGE_PREFERENCES_UPDATED': 'blue',
    'MESSAGE_UNSUBSCRIBED': 'orange',
    'MESSAGE_TYPE_UNSUBSCRIBED': 'orange',

    // Response Monitoring events
    'RESPONSE_MONITORING_CHECK_TRIGGERED': 'blue',
    'RESPONSE_MONITORING_MESSAGE_GENERATED': 'cyan',
    'RESPONSE_MONITORING_MESSAGE_APPROVED': 'green',
    'RESPONSE_MONITORING_MESSAGE_REJECTED': 'red',
    'RESPONSE_MONITORING_MESSAGE_SENT': 'green',
    'RESPONSE_MONITORING_MESSAGE_REGENERATED': 'orange',
    'RESPONSE_MONITORING_TEST_EMAIL_SENT': 'purple',
    'RESPONSE_MONITORING_CONFIG_UPDATED': 'blue',
    'RESPONSE_MONITORING_SCHEDULE_UPDATED': 'orange',

    // AI Expert events
    'AI_EXPERT_CONVERSATION_CREATED': 'green',
    'AI_EXPERT_CONVERSATION_DELETED': 'red',
    'AI_EXPERT_MESSAGE_SENT': 'blue',
    'AI_EXPERT_DOCUMENT_UPLOADED': 'green',
    'AI_EXPERT_DOCUMENT_DELETED': 'red',
    'AI_EXPERT_CONFIG_UPDATED': 'blue',
    'AI_EXPERT_TOGGLED': 'orange',
    'AI_EXPERT_CREATED': 'green',

    // Brand Knowledge Base events
    'BRAND_KNOWLEDGE_PROFILE_UPDATED': 'blue',
    'BRAND_KNOWLEDGE_DOCUMENT_ADDED': 'green',
    'BRAND_KNOWLEDGE_DOCUMENT_UPDATED': 'blue',
    'BRAND_KNOWLEDGE_DOCUMENT_DELETED': 'red',

    // Embedded CRM events
    'CRM_COMPANY_CREATED': 'green',
    'CRM_COMPANY_UPDATED': 'blue',
    'CRM_COMPANY_LINKED': 'green',
    'CRM_COMPANY_UNLINKED': 'orange',
    'CRM_STAGE_CHANGED': 'purple',
    'CRM_NOTE_ADDED': 'cyan',
    'CRM_TASK_CREATED': 'blue',
    'CRM_TASK_COMPLETED': 'green',
    'CRM_SHEET_SYNC_COMPLETED': 'geekblue',
    'CRM_SHEET_SYNC_FAILED': 'red',
    'CRM_EMAIL_SENT': 'cyan',
    'PLATFORM_EMAIL_SENT': 'geekblue',
    'CRM_EMAIL_RECEIVED': 'blue',
    'CRM_INBOX_SYNCED': 'geekblue',
    'CRM_REMINDER_SENT': 'gold',

    // The Hypothesis events (SYR-90)
    'HYPOTHESIS_RESEARCH_STARTED': 'blue',
    'HYPOTHESIS_RESEARCH_COMPLETED': 'green',
    'HYPOTHESIS_RESEARCH_INSUFFICIENT_DATA': 'orange',
    // Gold, not red: this is a request for input, not a failure.
    'HYPOTHESIS_AWAITING_REVIEWS': 'gold',
    'HYPOTHESIS_RESEARCH_FAILED': 'red',
    'HYPOTHESIS_FORMED': 'purple',
    'HYPOTHESIS_SUBJECT_DELETED': 'red',
    'HYPOTHESIS_TEASE_SENT': 'cyan',
    'HYPOTHESIS_DISMISSED': 'default',
    'HYPOTHESIS_CONFIRMED': 'green',
    'HYPOTHESIS_OVERTURNED': 'gold',
    'HYPOTHESIS_REVEAL_SENT': 'cyan',

    // Auto-Concierge (SYR-105)
    'CONCIERGE_PACK_QUEUED': 'default',
    'CONCIERGE_PACK_GENERATED': 'purple',
    'CONCIERGE_PACK_GENERATION_FAILED': 'red',
    'CONCIERGE_PACK_EDITED': 'blue',
    'CONCIERGE_PACK_SENT': 'green',
    'CONCIERGE_PACK_KILLED': 'default',
    'CONCIERGE_IDENTITY_VERIFICATION_SET': 'gold',
    'CONCIERGE_OFFER_SET': 'cyan',

    // Charter Cohort (SYR-106)
    'CHARTER_APPLICATION_SUBMITTED': 'blue',
    'CHARTER_APPLICATION_SPAM_REJECTED': 'default',
    'CHARTER_APPLICATION_ACCEPTED': 'green',
    'CHARTER_APPLICATION_HELD': 'orange',
    'CHARTER_APPLICATION_UNSUPPORTED': 'default',
    'CHARTER_APPLICATION_UNREVIEWED': 'blue',
    'CHARTER_ORGANISATION_PROVISIONED': 'gold',
    'CHARTER_TRIAL_START_BLOCKED': 'red',

    // Pricing strategy (SYR-109)
    'PAID_SIGNUP_COMPLETED': 'gold',

    // Public Hypothesis lead generator (SYR-107)
    'PUBLIC_HYPOTHESIS_REQUESTED': 'blue',
    'PUBLIC_HYPOTHESIS_PIN_VERIFIED': 'geekblue',
    'PUBLIC_HYPOTHESIS_QUEUED_FOR_REVIEW': 'orange',
    'PUBLIC_HYPOTHESIS_BLOCKED': 'default',
    'PUBLIC_HYPOTHESIS_GENERATED': 'purple',
    'PUBLIC_HYPOTHESIS_SERVED_CACHED': 'cyan',
    'PUBLIC_HYPOTHESIS_RESULT_SENT': 'green',
    'PUBLIC_HYPOTHESIS_INSUFFICIENT_DATA': 'gold',
    'PUBLIC_HYPOTHESIS_FAILED': 'red',
    'PUBLIC_HYPOTHESIS_SPAM_REJECTED': 'default',
    'PUBLIC_HYPOTHESIS_UNSUBSCRIBED': 'volcano',
    'PUBLIC_HYPOTHESIS_LEAD_TO_CRM': 'gold',
    'PUBLIC_HYPOTHESIS_LEAD_DELETED': 'red',
    'PUBLIC_HYPOTHESIS_RESULT_LINK_COPIED': 'geekblue',
    'HYPOTHESIS_PROVIDER_DEGRADED': 'volcano',
    'PUBLIC_HYPOTHESIS_VIDEO_PLAYED': 'geekblue',

    // Email delivery lifecycle events (SES webhook)
    'EMAIL_DELIVERED': 'green',
    'EMAIL_OPENED': 'cyan',
    'EMAIL_CLICKED': 'geekblue',
    'EMAIL_BOUNCED': 'red',
    'EMAIL_COMPLAINT_RECEIVED': 'volcano',

    // Dashboard AI events
    'DASHBOARD_AI_INSIGHT_GENERATED': 'cyan',
    'DASHBOARD_AI_CONFIG_UPDATED': 'blue',
    'DASHBOARD_AI_CACHE_CLEARED': 'orange',

    // Advice AI events
    'ADVICE_AI_INSIGHT_GENERATED': 'cyan',
    'ADVICE_AI_CONFIG_UPDATED': 'blue',
    'ADVICE_AI_CACHE_CLEARED': 'orange',

    // Orbital AI events
    'ORBITAL_AI_ITEMS_GENERATED': 'cyan',
    'ORBITAL_AI_CONFIG_UPDATED': 'blue',
    'ORBITAL_AI_CACHE_CLEARED': 'orange',

    // Building Blocks AI events
    'BUILDING_BLOCKS_AI_INSIGHTS_GENERATED': 'cyan',
    'BUILDING_BLOCKS_AI_CONFIG_UPDATED': 'blue',
    'BUILDING_BLOCKS_AI_CACHE_CLEARED': 'orange',

    // AI System Config events
    'AI_SYSTEM_CONFIG_UPDATED': 'blue',
    'AI_PROVIDER_TEST_SUCCESS': 'green',
    'AI_PROVIDER_TEST_FAILED': 'red',
    'AI_PROVIDER_FALLBACK_USED': 'orange',

    // AI Quota events
    'AI_QUOTA_ALERT_SENT': 'orange',
    'AI_QUOTA_WARNING_SENT': 'gold',
    'AI_QUOTA_EXCEEDED': 'red',
    'AI_QUOTA_UPDATED': 'blue',
    'AI_QUOTA_RESET': 'green',

    // Brand Analytics events
    'BRAND_ANALYTICS_VIEWED': 'cyan',
    'BRAND_ANALYTICS_DRILLDOWN_ACCESSED': 'blue',

    // Custom Brand Charts events
    'CUSTOM_CHART_CREATED': 'green',
    'CUSTOM_CHART_UPDATED': 'blue',
    'CUSTOM_CHART_DELETED': 'red',
    'CUSTOM_CHARTS_REORDERED': 'orange',

    // Scheduled Job Lock events
    'SCHEDULED_JOB_STARTED': 'green',
    'SCHEDULED_JOB_COMPLETED': 'green',
    'SCHEDULED_JOB_FAILED': 'red',
    'SCHEDULED_JOB_LOCK_ACQUIRED': 'blue',
    'SCHEDULED_JOB_LOCK_RELEASED': 'default',
    'SCHEDULED_JOB_LOCK_FAILED': 'orange',
    'SCHEDULED_JOB_LOCK_EXPIRED': 'orange',
    'SCHEDULED_JOB_FORCE_UNLOCKED': 'red',

    // CloudWatch Logs events
    'CLOUDWATCH_LOGS_ACCESSED': 'cyan',
    'CLOUDWATCH_LOGS_TAILED': 'blue',
    'CLOUDWATCH_LOGS_TAIL_ENDED': 'default',
    'CLOUDWATCH_LOGS_SEARCHED': 'purple',

    // Support Ticket events
    'TICKET_CREATED': 'green',
    'TICKET_UPDATED': 'blue',
    'TICKET_COMMENT_ADDED': 'cyan',
    'TICKET_ATTACHMENT_UPLOADED': 'green',
    'TICKET_ATTACHMENT_DELETED': 'red',

    // Free Trial events
    'FREE_TRIAL_STARTED': 'green',
    'FREE_TRIAL_RESPONSE_REQUIREMENT_MET': 'green',
    'FREE_TRIAL_RESPONSE_REQUIREMENT_FAILED': 'red',
    'FREE_TRIAL_DOWNGRADED': 'red',
    'FREE_TRIAL_CONVERTED': 'green',
    'FREE_TRIAL_EXPIRED': 'orange',
    'FREE_TRIAL_NOTIFICATION_SENT': 'blue',
    'FREE_TRIAL_RESET': 'purple',
    'FREE_TRIAL_NOTIFICATION_SUPPRESSED': 'orange',

    // Waitlist events
    'WAITLIST_ENTRY_CREATED': 'blue',
    'WAITLIST_ENTRY_UPDATED': 'cyan',
    'WAITLIST_STATUS_CHANGED': 'orange',

    // Corporate Site Editor events
    'CORPORATE_SITE_PUBLISHED': 'green',
    'CORPORATE_SITE_REVERTED': 'orange',

    // Response Integrity events (SYR-48)
    'RESPONSE_INTEGRITY_SCORED': 'cyan',
    'RESPONSE_INTEGRITY_FLAGGED': 'orange',
    'RESPONSE_INTEGRITY_REJECTED_MANUALLY': 'red',
    'RESPONSE_INTEGRITY_CONFIRMED_CLEAN': 'green',
    'RESPONSE_INTEGRITY_RESCORED': 'blue',
    'RESPONSE_INTEGRITY_BULK_REJECTED': 'red',
    'RESPONSE_INTEGRITY_BULK_CONFIRMED_CLEAN': 'green',
    'RESPONSE_INTEGRITY_BULK_RESCORED': 'blue',
    'RESPONSE_INTEGRITY_DETECTOR_CONFIG_UPDATED': 'orange',
    'RESPONSE_INTEGRITY_PROFILE_UPDATED': 'orange',
    'RESPONSE_INTEGRITY_LLM_BUDGET_ALERT': 'orange',
    'RESPONSE_INTEGRITY_LLM_BUDGET_EXHAUSTED': 'red',
    'RESPONSE_INTEGRITY_EVIDENCE_ARCHIVED': 'default',

    // Panel Chargeback events (SYR-48)
    'PANEL_CHARGEBACK_PREPARED': 'gold',
    'PANEL_CHARGEBACK_SUBMITTED': 'blue',
    'PANEL_CHARGEBACK_RESOLVED': 'green',
};

// Custom labels for better readability (optional - for special cases)
export const EVENT_TYPE_LABELS: Record<string, string> = {
    // AI-related events with proper capitalization
    'AI_EXPERT_CONVERSATION_CREATED': 'AI Expert Conversation Created',
    'AI_EXPERT_CONVERSATION_DELETED': 'AI Expert Conversation Deleted',
    'AI_EXPERT_MESSAGE_SENT': 'AI Expert Message Sent',
    'AI_EXPERT_DOCUMENT_UPLOADED': 'AI Expert Document Uploaded',
    'AI_EXPERT_DOCUMENT_DELETED': 'AI Expert Document Deleted',
    'AI_EXPERT_CONFIG_UPDATED': 'AI Expert Config Updated',
    'AI_EXPERT_TOGGLED': 'AI Expert Toggled',
    'AI_EXPERT_CREATED': 'AI Expert Created',
    'BRAND_KNOWLEDGE_PROFILE_UPDATED': 'Brand Knowledge Updated',
    'BRAND_KNOWLEDGE_DOCUMENT_ADDED': 'Brand Knowledge Document Added',
    'BRAND_KNOWLEDGE_DOCUMENT_UPDATED': 'Brand Knowledge Document Updated',
    'BRAND_KNOWLEDGE_DOCUMENT_DELETED': 'Brand Knowledge Document Deleted',
    'CRM_COMPANY_CREATED': 'CRM Account Created',
    'CRM_COMPANY_UPDATED': 'CRM Account Updated',
    'CRM_COMPANY_LINKED': 'CRM Account Linked to Organisation',
    'CRM_COMPANY_UNLINKED': 'CRM Account Unlinked',
    'CRM_STAGE_CHANGED': 'CRM Pipeline Stage Changed',
    'CRM_NOTE_ADDED': 'CRM Note Added',
    'CRM_TASK_CREATED': 'CRM Task Created',
    'CRM_TASK_COMPLETED': 'CRM Task Completed',
    'PLATFORM_EMAIL_SENT': 'Platform Email Sent',
    'SURVEY_LINK_NOT_FOUND': 'Survey Link Broken (Dead)',
    'SURVEY_LINK_SALVAGED': 'Survey Link Broken (Recovered)',
    'CRM_SHEET_SYNC_COMPLETED': 'CRM Sheet Sync Completed',
    'CRM_SHEET_SYNC_FAILED': 'CRM Sheet Sync Failed',
    'CRM_EMAIL_SENT': 'CRM Email Sent',
    'CRM_EMAIL_RECEIVED': 'CRM Email Received',
    'CRM_INBOX_SYNCED': 'CRM Inbox Synced',
    'CRM_REMINDER_SENT': 'CRM Reminder Sent',
    'HYPOTHESIS_RESEARCH_STARTED': 'Hypothesis Research Started',
    'HYPOTHESIS_RESEARCH_COMPLETED': 'Hypothesis Research Completed',
    'HYPOTHESIS_RESEARCH_INSUFFICIENT_DATA': 'Hypothesis Research Insufficient Data',
    'HYPOTHESIS_AWAITING_REVIEWS': 'Hypothesis Awaiting Review Sources',
    'HYPOTHESIS_RESEARCH_FAILED': 'Hypothesis Research Failed',
    'HYPOTHESIS_FORMED': 'Hypothesis Formed',
    'HYPOTHESIS_SUBJECT_DELETED': 'Hypothesis Research Deleted',
    'HYPOTHESIS_TEASE_SENT': 'Hypothesis Tease Email Sent',
    'HYPOTHESIS_DISMISSED': 'Hypothesis Dismissed',
    'HYPOTHESIS_CONFIRMED': 'Hypothesis Confirmed',
    'HYPOTHESIS_OVERTURNED': 'Hypothesis Overturned',
    'HYPOTHESIS_REVEAL_SENT': 'Hypothesis Reveal Email Sent',
    'CONCIERGE_PACK_QUEUED': 'Concierge Pack Queued',
    'CONCIERGE_PACK_GENERATED': 'Concierge Pack Generated',
    'CONCIERGE_PACK_GENERATION_FAILED': 'Concierge Pack Generation Failed',
    'CONCIERGE_PACK_EDITED': 'Concierge Pack Edited',
    'CONCIERGE_PACK_SENT': 'Concierge Pack Sent',
    'CONCIERGE_PACK_KILLED': 'Concierge Pack Killed',
    'CONCIERGE_IDENTITY_VERIFICATION_SET': 'Concierge Identity Verification Set',
    'CONCIERGE_OFFER_SET': 'Concierge Offer Set by Brand',
    'CHARTER_APPLICATION_SUBMITTED': 'Charter Application Submitted',
    'CHARTER_APPLICATION_SPAM_REJECTED': 'Charter Application Rejected as Spam',
    'CHARTER_APPLICATION_ACCEPTED': 'Charter Application Accepted',
    'CHARTER_APPLICATION_HELD': 'Charter Application Held',
    'CHARTER_APPLICATION_UNSUPPORTED': 'Charter Application Moved to Waitlist',
    'CHARTER_APPLICATION_UNREVIEWED': 'Charter Application Returned to Pending',
    'CHARTER_ORGANISATION_PROVISIONED': 'Charter Organisation Provisioned',
    'CHARTER_TRIAL_START_BLOCKED': 'Charter Trial Start Blocked',
    'PAID_SIGNUP_COMPLETED': 'Paid Signup Completed',
    'PUBLIC_HYPOTHESIS_REQUESTED': 'Hypothesis Tool Request',
    'PUBLIC_HYPOTHESIS_PIN_VERIFIED': 'Hypothesis Tool Email Verified',
    'PUBLIC_HYPOTHESIS_QUEUED_FOR_REVIEW': 'Hypothesis Tool Queued for Review',
    'PUBLIC_HYPOTHESIS_BLOCKED': 'Hypothesis Tool Request Blocked',
    'PUBLIC_HYPOTHESIS_GENERATED': 'Hypothesis Tool Result Generated',
    'PUBLIC_HYPOTHESIS_SERVED_CACHED': 'Hypothesis Tool Served Cached Result',
    'PUBLIC_HYPOTHESIS_RESULT_SENT': 'Hypothesis Tool Result Emailed',
    'PUBLIC_HYPOTHESIS_INSUFFICIENT_DATA': 'Hypothesis Tool Not Enough Data',
    'PUBLIC_HYPOTHESIS_FAILED': 'Hypothesis Tool Run Failed',
    'PUBLIC_HYPOTHESIS_SPAM_REJECTED': 'Hypothesis Tool Rejected as Spam',
    'PUBLIC_HYPOTHESIS_UNSUBSCRIBED': 'Hypothesis Tool Unsubscribed',
    'PUBLIC_HYPOTHESIS_LEAD_TO_CRM': 'Hypothesis Tool Lead Added to CRM',
    'PUBLIC_HYPOTHESIS_LEAD_DELETED': 'Hypothesis Tool Lead Deleted',
    'PUBLIC_HYPOTHESIS_RESULT_LINK_COPIED': 'Hypothesis Result Link Copied by Admin',
    'HYPOTHESIS_PROVIDER_DEGRADED': 'Hypothesis Data Source Unavailable',
    'PUBLIC_HYPOTHESIS_VIDEO_PLAYED': 'Hypothesis Explainer Video Played',
    'EMAIL_DELIVERED': 'Email Delivered',
    'EMAIL_OPENED': 'Email Opened',
    'EMAIL_CLICKED': 'Email Link Clicked',
    'EMAIL_BOUNCED': 'Email Bounced',
    'EMAIL_COMPLAINT_RECEIVED': 'Email Complaint Received',
    'DASHBOARD_AI_INSIGHT_GENERATED': 'Dashboard AI Insight Generated',
    'DASHBOARD_AI_CONFIG_UPDATED': 'Dashboard AI Config Updated',
    'DASHBOARD_AI_CACHE_CLEARED': 'Dashboard AI Cache Cleared',
    'ADVICE_AI_INSIGHT_GENERATED': 'Advice AI Insight Generated',
    'ADVICE_AI_CONFIG_UPDATED': 'Advice AI Config Updated',
    'ADVICE_AI_CACHE_CLEARED': 'Advice AI Cache Cleared',
    'ORBITAL_AI_ITEMS_GENERATED': 'Orbital AI Items Generated',
    'ORBITAL_AI_CONFIG_UPDATED': 'Orbital AI Config Updated',
    'ORBITAL_AI_CACHE_CLEARED': 'Orbital AI Cache Cleared',
    'BUILDING_BLOCKS_AI_INSIGHTS_GENERATED': 'Building Blocks AI Insights Generated',
    'BUILDING_BLOCKS_AI_CONFIG_UPDATED': 'Building Blocks AI Config Updated',
    'BUILDING_BLOCKS_AI_CACHE_CLEARED': 'Building Blocks AI Cache Cleared',

    // AI System Config events with proper capitalization
    'AI_SYSTEM_CONFIG_UPDATED': 'AI System Config Updated',
    'AI_PROVIDER_TEST_SUCCESS': 'AI Provider Test Success',
    'AI_PROVIDER_TEST_FAILED': 'AI Provider Test Failed',
    'AI_PROVIDER_FALLBACK_USED': 'AI Provider Fallback Used',

    // AI Quota events with proper capitalization
    'AI_QUOTA_ALERT_SENT': 'AI Quota Alert Sent',
    'AI_QUOTA_WARNING_SENT': 'AI Quota Warning Sent',
    'AI_QUOTA_EXCEEDED': 'AI Quota Exceeded',
    'AI_QUOTA_UPDATED': 'AI Quota Updated',
    'AI_QUOTA_RESET': 'AI Quota Reset',

    // API-related events with proper capitalization
    'API_KEY_CREATED': 'API Key Created',
    'API_KEY_REVOKED': 'API Key Revoked',
    'PUBLIC_API_PARTICIPANTS_FETCHED': 'API Participants Fetched',

    // Free Trial events
    'FREE_TRIAL_STARTED': 'Free Trial Started',
    'FREE_TRIAL_RESPONSE_REQUIREMENT_MET': 'Free Trial Requirement Met',
    'FREE_TRIAL_RESPONSE_REQUIREMENT_FAILED': 'Free Trial Requirement Failed',
    'FREE_TRIAL_DOWNGRADED': 'Free Trial Downgraded',
    'FREE_TRIAL_CONVERTED': 'Free Trial Converted',
    'FREE_TRIAL_EXPIRED': 'Free Trial Expired',
    'FREE_TRIAL_NOTIFICATION_SENT': 'Free Trial Notification Sent',
    'FREE_TRIAL_RESET': 'Free Trial Reset',
    'FREE_TRIAL_NOTIFICATION_SUPPRESSED': 'Free Trial Notification Held (Concierge)',

    // Waitlist events
    'WAITLIST_ENTRY_CREATED': 'Waitlist Entry Created',
    'WAITLIST_ENTRY_UPDATED': 'Waitlist Entry Updated',
    'WAITLIST_STATUS_CHANGED': 'Waitlist Status Changed',

    // Corporate Site Editor events
    'CORPORATE_SITE_PUBLISHED': 'Corporate Site Published',
    'CORPORATE_SITE_REVERTED': 'Corporate Site Reverted',

    // Response Integrity events (SYR-48)
    'RESPONSE_INTEGRITY_SCORED': 'Response Scored',
    'RESPONSE_INTEGRITY_FLAGGED': 'Response Flagged',
    'RESPONSE_INTEGRITY_REJECTED_MANUALLY': 'Response Rejected Manually',
    'RESPONSE_INTEGRITY_CONFIRMED_CLEAN': 'Response Confirmed Clean',
    'RESPONSE_INTEGRITY_RESCORED': 'Response Rescored',
    'RESPONSE_INTEGRITY_BULK_REJECTED': 'Bulk Reject',
    'RESPONSE_INTEGRITY_BULK_CONFIRMED_CLEAN': 'Bulk Confirm Clean',
    'RESPONSE_INTEGRITY_BULK_RESCORED': 'Bulk Rescore',
    'RESPONSE_INTEGRITY_DETECTOR_CONFIG_UPDATED': 'Detector Config Updated',
    'RESPONSE_INTEGRITY_PROFILE_UPDATED': 'Integrity Settings Updated',
    'RESPONSE_INTEGRITY_LLM_BUDGET_ALERT': 'LLM Budget Alert (80%)',
    'RESPONSE_INTEGRITY_LLM_BUDGET_EXHAUSTED': 'LLM Budget Exhausted',
    'RESPONSE_INTEGRITY_EVIDENCE_ARCHIVED': 'Evidence Archived',

    // Panel Chargeback events (SYR-48)
    'PANEL_CHARGEBACK_PREPARED': 'Panel Chargeback Prepared',
    'PANEL_CHARGEBACK_SUBMITTED': 'Panel Chargeback Submitted',
    'PANEL_CHARGEBACK_RESOLVED': 'Panel Chargeback Resolved',
};

export const formatEventType = (eventType: string): string => {
    // Check if we have a custom label first
    if (EVENT_TYPE_LABELS[eventType]) {
        return EVENT_TYPE_LABELS[eventType];
    }

    // Otherwise use the default formatting
    return eventType.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
};